/**
 * Utility for tracking Virtual Try On submission attempts across all variants
 * Stores count in localStorage with a cumulative counter
 */

const STORAGE_KEY = 'virtualTryOnSubmissionCount';
const MAX_SUBMISSIONS = 5;

/**
 * Event name for listening to submission count changes
 */
export const VIRTUAL_TRY_ON_COUNT_CHANGE_EVENT = 'virtualTryOnSubmissionCountChanged';

const getLocalStorage = (): Storage | null => {
	if (globalThis.window === undefined) {
		return null;
	}
	try {
		return globalThis.localStorage;
	} catch {
		return null;
	}
};

/**
 * Dispatches a custom event to notify all listeners of submission count changes
 */
const dispatchCountChangeEvent = (): void => {
	if (globalThis.window === undefined) return;
	globalThis.dispatchEvent(new CustomEvent(VIRTUAL_TRY_ON_COUNT_CHANGE_EVENT));
};

/**
 * Gets the current submission count from localStorage
 * @returns The current submission count (0-5)
 */
export const getVirtualTryOnSubmissionCount = (): number => {
	const storage = getLocalStorage();
	if (storage === null) {
		return 0;
	}

	try {
		const storedValue = storage.getItem(STORAGE_KEY);
		if (storedValue === null) {
			return 0;
		}
		const count = Number.parseInt(storedValue, 10);
		return Number.isNaN(count) ? 0 : Math.max(0, Math.min(count, MAX_SUBMISSIONS));
	} catch (error) {
		console.error('Error reading Virtual Try On submission count:', error);
		return 0;
	}
};

export const incrementVirtualTryOnSubmissionCount = (): number => {
	const storage = getLocalStorage();
	if (storage === null) {
		return 0;
	}

	try {
		const currentCount = getVirtualTryOnSubmissionCount();
		const newCount = Math.min(currentCount + 1, MAX_SUBMISSIONS);
		storage.setItem(STORAGE_KEY, newCount.toString());
		dispatchCountChangeEvent();
		return newCount;
	} catch (error) {
		console.error('Error incrementing Virtual Try On submission count:', error);
		return getVirtualTryOnSubmissionCount();
	}
};

export const hasReachedVirtualTryOnSubmissionLimit = (): boolean => {
	return getVirtualTryOnSubmissionCount() >= MAX_SUBMISSIONS;
};

export const resetVirtualTryOnSubmissionCount = (): void => {
	const storage = getLocalStorage();
	if (storage === null) {
		return;
	}

	try {
		storage.removeItem(STORAGE_KEY);
		dispatchCountChangeEvent();
	} catch (error) {
		console.error('Error resetting Virtual Try On submission count:', error);
	}
};

export const getRemainingVirtualTryOnSubmissions = (): number => {
	return Math.max(0, MAX_SUBMISSIONS - getVirtualTryOnSubmissionCount());
};
